//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { BytesLike, Contract, ethers, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, ChainError, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { Erc20Abi, PoolAbi, PoolDynamicAbi, PoolFlexAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { ERC20Wei, erc20WeiToCommaDecString, otAssert } from '@shared/conversions'

import { AlertCTO } from '@shared/AlertCTO'
import { Pool } from '@typechain-types/index'

import { alertActions } from '@react/redux/alertSlice'

import { dateToEthDate, ProviderReadOnly } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { approveDepositToPool } from './approveDepositToPoolTx'

export const depositToPoolTx =
  (props: { poolAddr: EthAddr; amountWei: ERC20Wei }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.POOL_DEPOSIT
    const { poolAddr, amountWei } = props

    try {
      logOT.debug('depositToPool 1')

      const { webSocket, userConnection } = getState().chainState
      const { chainConfigurationName, providerReadOnly } = getState().chainState.chainConfiguration
      otAssert(providerReadOnly, 'providerReadOnly is not defined')
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.LENDER)

      await depositToPool(webSocket, signer as Signer, providerReadOnly, poolAddr, amountWei, chainConfigurationName)

      dispatch(poolActions.depositToPoolSuccess({ poolAddr, amountWei }))

      const depositSuccess: AlertCTO = {
        alertLevel: AlertLevelEnum.SUCCESS,
        title: `Successfully invested ${erc20WeiToCommaDecString(amountWei)} `,
        description: '',
        sourceObjectAddr: poolAddr,
        createdAt: dateToEthDate(new Date()),
        dueDateTime: 0,
      }
      dispatch(alertActions.addAlert(depositSuccess))
    } catch (e: any) {
      dispatch(poolActions.depositToPoolFail({ poolAddr, amountWei }))

      await dispatchError(dispatch, e, activity, 1009)
    }
  }

export const depositAndApproveToPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  depositAmountWei: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  await approveDepositToPool(
    webSocket,
    lenderSigner,
    providerReadOnly,
    poolAddr,
    depositAmountWei,
    chainConfigurationName
  )
  await depositToPool(webSocket, lenderSigner, providerReadOnly, poolAddr, depositAmountWei, chainConfigurationName)
}
export const depositToPool = async (
  webSocket: WebSocket | undefined,
  lenderSigner: Signer,
  providerReadOnly: ProviderReadOnly,
  poolAddr: EthAddr,
  depositAmountWei: ERC20Wei,
  chainConfigurationName: string
): Promise<void> => {
  try {
    const uuid = txUUID()
    webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_DEPOSIT, poolAddr, uuid, chainConfigurationName))
    logOT.debug('depositOnlyToPool', poolAddr, !!lenderSigner, depositAmountWei)
    // Note: Using Pool since the signature for liquidityAssetAddr() is the same across all pool types
    const poolBaseContract = new Contract(poolAddr, PoolAbi, lenderSigner) as unknown as Pool

    const liquidityAsset = (await poolBaseContract.liquidityAssetAddr()) as EthAddr

    logOT.debug('depositOnlyToPoolliquidityAsset ', liquidityAsset)
    const erc20Contract = new Contract(liquidityAsset, Erc20Abi, providerReadOnly)
    const lenderAddr: EthAddr = await lenderSigner.getAddress()

    logOT.debug('depositOnlyToPool', lenderAddr, poolAddr, liquidityAsset)
    const currentAllowance = await erc20Contract.allowance(lenderAddr, poolAddr)

    logOT.debug('depositOnlyToPool currentAllowance', currentAllowance)
    logOT.debug('depositToPool currentAllowance', lenderAddr, poolAddr, currentAllowance.toString())
    logOT.debug('depositToPool Sending Deposit')

    // Note: Using Pool since the signature for liquidityAssetAddr() is the same across all pool types
    const depositToPoolTx = (await poolBaseContract?.deposit(
      depositAmountWei,
      lenderAddr
    )) as unknown as ethers.ContractTransactionResponse

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.POOL_DEPOSIT,
        poolAddr,
        uuid,
        depositToPoolTx.hash as TxHash,
        chainConfigurationName
      )
    )

    logOT.debug('depositToPool Deposit Sent')
    const depositToPoolTx2 = await depositToPoolTx.wait(1)
    logOT.debug('depositToPool Deposit Waited')
    logOT.debug('depositToPool depositToPoolTx', depositToPoolTx2)
  } catch (e: unknown) {
    const error = e as ChainError

    if (error.reason === 'repriced') {
      return
    }

    if (error?.error?.data?.originalError) {
      const poolContract = new Contract(poolAddr, PoolAbi, providerReadOnly)
      const poolType = Number(await poolContract.poolType())

      logOT.debug('poolType', poolType)
      if (poolType === PoolTypeEnum.Dynamic) {
        logOT.debug("It's a flexible rate pool")
        const poolDynamicContract = new Contract(poolAddr, PoolDynamicAbi, providerReadOnly)
        const decodedError = poolDynamicContract.interface.parseError(
          error.error?.data?.originalError.data as unknown as BytesLike
        )
        logOT.debug('Decoded error Flex', decodedError)
        throw e
      } else if (poolType === PoolTypeEnum.FlexibleRate) {
        logOT.debug("It's a flexible rate pool")

        const poolFlexContract = new Contract(poolAddr, PoolFlexAbi, providerReadOnly)
        const decodedError = poolFlexContract.interface.parseError(
          error.error?.data?.originalError.data as unknown as BytesLike
        )
        logOT.error('Decoded error 11', decodedError)
        throw e
      } else {
        const decodedError = poolContract.interface.parseError(
          error.error?.data?.originalError.data as unknown as BytesLike
        )
        logOT.error('Decoded error 222', decodedError)
        throw e
      }
    }

    if (Object.keys(e as { error: ChainError }).includes('error')) {
      const error = (e as { error: ChainError }).error as ChainError

      logOT.error('depositToPool error 1', error.reason)

      throw error
    }

    logOT.error('depositToPool error 2', error.reason)

    throw e
  }
}
